import { createEntityAdapter, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '@/store/store';
import { PartialDeep } from 'type-fest';
import { FuseFlatNavItemType, FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';
import FuseNavItemModel from '@fuse/core/FuseNavigation/models/FuseNavItemModel';
import navigationConfig from '@/configs/navigationConfig';

const navigationAdapter = createEntityAdapter<FuseFlatNavItemType>();

const emptyInitialState = navigationAdapter.getInitialState([]);

const initialState = navigationAdapter.upsertMany(
  emptyInitialState,
  FuseNavigationHelper.flattenNavigation(navigationConfig)
);

/**
 * Redux Thunk actions related to the navigation store state
 */
/**
 * Appends a navigation item to the navigation store state.
 */
export const appendNavigationItem =
  (item: FuseNavItemType, parentId?: string | null): AppThunk =>
  async (dispatch, getState) => {
    const AppState = getState();
    const navigation = FuseNavigationHelper.unflattenNavigation(selectNavigationAll(AppState));

    dispatch(setNavigation(FuseNavigationHelper.appendNavItem(navigation, FuseNavItemModel(item), parentId)));

    return Promise.resolve();
  };

/**
 * Prepends a navigation item to the navigation store state.
 */
export const prependNavigationItem =
  (item: FuseNavItemType, parentId?: string | null): AppThunk =>
  async (dispatch, getState) => {
    const AppState = getState();
    const navigation = FuseNavigationHelper.unflattenNavigation(selectNavigationAll(AppState));

    dispatch(setNavigation(FuseNavigationHelper.prependNavItem(navigation, FuseNavItemModel(item), parentId)));

    return Promise.resolve();
  };

/**
 * Adds a navigation item to the navigation store state at the specified index.
 */
export const updateNavigationItem =
  (id: string, item: PartialDeep<FuseNavItemType>): AppThunk =>
  async (dispatch, getState) => {
    const AppState = getState();
    const navigation = FuseNavigationHelper.unflattenNavigation(selectNavigationAll(AppState));

    dispatch(setNavigation(FuseNavigationHelper.updateNavItem(navigation, id, item)));

    return Promise.resolve();
  };

/**
 * Removes a navigation item from the navigation store state.
 */
export const removeNavigationItem =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    const AppState = getState();
    const navigation = FuseNavigationHelper.unflattenNavigation(selectNavigationAll(AppState));

    dispatch(setNavigation(FuseNavigationHelper.removeNavItem(navigation, id)));

    return Promise.resolve();
  };

export const {
  selectAll: selectNavigationAll,
  selectIds: selectNavigationIds,
  selectById: selectNavigationItemById
} = navigationAdapter.getSelectors<RootState>((state) => state.navigation);

/**
 * The navigation slice
 */
export const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setNavigation(state, action: PayloadAction<FuseNavItemType[]>) {
      return navigationAdapter.setAll(state, FuseNavigationHelper.flattenNavigation(action.payload));
    },
    resetNavigation: () => initialState
  }
});

export const { setNavigation, resetNavigation } = navigationSlice.actions;

export type navigationSliceType = typeof navigationSlice;

export default navigationSlice.reducer;

export const fetchApplicationsAndSetNavigation = createAsyncThunk(
  'navigation/fetchApplications',
  async (_, { dispatch }) => {
    try {
      // Fetch applications from the mock API
      const response = await axios.get('/api/admin/applications/items');
      const applications = response.data;

      // Transform the fetched applications into navigation items
      const applicationNavItems = applications.map((app: any) => ({
        id: app.id,
        title: app.name,
        type: 'item',
        icon: 'heroicons-outline:archive-box', // Default icon
        url: `/apps/${app.id}` // Navigate to application-specific routes
      }));

      // Add these items under the `Apps` group
      const updatedNavigationConfig = navigationConfig.map((item) => {
        if (item.id === 'apps') {
          return {
            ...item,
            children: applicationNavItems
          };
        }

        return item;
      });

      // Update navigation in Redux
      dispatch(setNavigation(updatedNavigationConfig));
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  }
);
